import { CODE_OK, type ApiEnvelope } from './envelope'
import { ApiError, TransportError } from './errors'

/**
 * 三端共用的 HTTP 客户端。
 *
 * <h2>🔴 它只做四件事，每一件都是「不做就一定出错」的</h2>
 * <ol>
 *   <li>拆信封：`code === 0` 取 `data`，否则抛 {@link ApiError}。
 *       ⛔ 只看 `response.ok` 会把每一个业务失败当成成功（后端业务失败是 HTTP 200）。</li>
 *   <li>把「没到后端」与「后端给了判定」分成两种异常，见 errors.ts。</li>
 *   <li>超时 —— fetch 默认**永不超时**，一个吊死的请求会让界面永远停在加载态。</li>
 * </ol>
 *
 * <h2>🔴 它**不**知道开发期租户头这回事</h2>
 * 早先的版本在这里直接 `import { devTenantHeaders }`，结果是：
 * 只要有人 new 了一个客户端，那个开发期请求头的代码就**留在生产产物里**
 * （实测过：`X-Tenant-Id` 字面量确实出现在 dist 的 js 里）。
 * ⇒ 改成由调用方经 {@link HttpClientOptions.extraHeaders} 注入，
 * 而 seller 只在 `import.meta.env.DEV` 下注入 ——
 * 这样生产构建里**连引用都没有**，打包器能把整个 devTenant 模块摇掉。
 *
 * <h2>⛔ 它刻意不做的事</h2>
 * <ul>
 *   <li>**不打日志**：本工程有接口的响应体里带着入站通道 token（`PushSetupVO.callbackPath`），
 *       请求体里带着店铺密钥。把 body 打进 console 等于把它们写进任何一份浏览器日志/录屏。</li>
 *   <li>**不重试**：哪些码可以重试是业务判断（12023 缺权限重试永远不成功），
 *       在传输层统一重试等于替业务做了错误决定。</li>
 *   <li>**不按码翻译文案**：见 erp-web/README §四。</li>
 * </ul>
 */

export interface HttpClientOptions {
  /** 请求前缀。开发期留空 —— Vite 代理把 `/api` 转给 localhost:8080，同源，不需要 CORS */
  baseUrl?: string
  /** 默认 15s。⚠️ 凭据体检会真的去调平台，那条路要单独放宽 */
  timeoutMs?: number
  /**
   * 每次请求额外附加的头。
   *
   * 🔴 现在唯一的用途是**开发期租户头**，且调用方必须自己用 `import.meta.env.DEV`
   * 把它挡在开发构建里。登录接上之后这个选项与 devTenant.ts 一起删。
   */
  extraHeaders?: () => Record<string, string>
}

export interface RequestOptions {
  /** 覆盖本次请求的超时（毫秒） */
  timeoutMs?: number
  signal?: AbortSignal
}

export interface HttpClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  del<T>(path: string, options?: RequestOptions): Promise<T>
}

const DEFAULT_TIMEOUT_MS = 15_000

/** User-facing copy for requests that never reached an API response. Keep diagnostics out of UI text. */
export const NETWORK_UNREACHABLE_MESSAGE = '网络连接失败，暂时无法连接服务器。请检查网络后重试。'

export function createHttpClient(options: HttpClientOptions = {}): HttpClient {
  const baseUrl = options.baseUrl ?? ''
  const defaultTimeout = options.timeoutMs ?? DEFAULT_TIMEOUT_MS

  async function request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    body: unknown,
    req: RequestOptions = {},
  ): Promise<T> {
    const controller = new AbortController()
    const timeoutMs = req.timeoutMs ?? defaultTimeout
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    if (req.signal) {
      req.signal.addEventListener('abort', () => controller.abort(), { once: true })
    }

    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(options.extraHeaders ? options.extraHeaders() : {}),
    }
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    let response: Response
    try {
      response = await fetch(baseUrl + path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        // Web 端用 cookie 会话（README §二②：不要为了和插件统一而降级成 localStorage 里的 Bearer）
        credentials: 'same-origin',
        signal: controller.signal,
      })
    } catch {
      if (controller.signal.aborted) {
        throw new TransportError(
          `请求超过 ${Math.round(timeoutMs / 1000)} 秒没有响应，已中止。` +
            '这不代表后端没有执行 —— 写操作请先去列表确认结果，不要直接重发。',
          null,
        )
      }
      throw new TransportError(NETWORK_UNREACHABLE_MESSAGE, null)
    } finally {
      clearTimeout(timer)
    }

    const text = await response.text()
    let envelope: ApiEnvelope<T>
    try {
      envelope = JSON.parse(text) as ApiEnvelope<T>
    } catch {
      // ⚠️ 不把 text 拼进消息：它可能是一份带 token 的响应，也可能是一整页 HTML 错误页
      throw new TransportError(
        `服务器返回的不是 JSON（HTTP ${response.status}）—— 通常是请求没打到后端而落到了别的服务上。`,
        response.status,
      )
    }

    if (typeof envelope?.code !== 'number') {
      throw new TransportError(
        `服务器返回的 JSON 不是统一响应信封（HTTP ${response.status}）。`,
        response.status,
      )
    }

    if (envelope.code !== CODE_OK) {
      // 🔴 message 原样透出，前端不改写
      throw new ApiError(
        envelope.code,
        envelope.message,
        response.status,
        envelope.traceId ?? null,
      )
    }

    // ⚠️ `data` 可以合法地为 null（`R.ok()`），由调用方的类型参数负责
    return envelope.data as T
  }

  return {
    get: <T>(path: string, o?: RequestOptions) => request<T>('GET', path, undefined, o),
    post: <T>(path: string, body?: unknown, o?: RequestOptions) =>
      request<T>('POST', path, body, o),
    put: <T>(path: string, body?: unknown, o?: RequestOptions) => request<T>('PUT', path, body, o),
    del: <T>(path: string, o?: RequestOptions) => request<T>('DELETE', path, undefined, o),
  }
}
